import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { fetchDashboardStats } from '../services/api'
import {
  IdeaIcon,
  ClipboardIcon,
  RocketIcon,
  CheckIcon,
  RefreshIcon,
} from './Icons'
import '../styles/Dashboard.css'

function ProjectProgressBar({ project }) {
  const [hoveredMilestone, setHoveredMilestone] = useState(null)

  // Calculate total width units (each task = 1 unit)
  const totalUnits = project.total_tasks || 1 // Avoid division by zero

  // Calculate milestone positions and widths based on task count
  let currentPosition = 0
  const milestonePositions = []

  // First, add unassigned tasks before first milestone
  if (project.unassigned_tasks > 0 && project.milestones.length > 0) {
    // Unassigned tasks go at the end (or we can distribute them)
  }

  // Calculate position for each milestone based on tasks before it
  project.milestones.forEach((milestone, index) => {
    const width = (milestone.task_count / totalUnits) * 100
    milestonePositions.push({
      ...milestone,
      position: currentPosition,
      width: width,
      index
    })
    currentPosition += width
  })

  // Add unassigned tasks at the end
  if (project.unassigned_tasks > 0) {
    const unassignedWidth = (project.unassigned_tasks / totalUnits) * 100
    milestonePositions.push({
      id: 'unassigned',
      title: 'Unassigned Tasks',
      completed: project.unassigned_completed === project.unassigned_tasks && project.unassigned_tasks > 0,
      task_count: project.unassigned_tasks,
      tasks_completed: project.unassigned_completed,
      position: currentPosition,
      width: unassignedWidth,
      index: project.milestones.length
    })
  }

  // If no milestones and no unassigned (shouldn't happen, but handle it)
  if (milestonePositions.length === 0) {
    milestonePositions.push({
      id: 'all',
      title: 'All Tasks',
      completed: project.completed_tasks === project.total_tasks,
      task_count: project.total_tasks,
      tasks_completed: project.completed_tasks,
      position: 0,
      width: 100,
      index: 0
    })
  }

  return (
    <div className="project-progress-item">
      <Link to={`/elise/plans/${project.id}`} className="project-progress-title">
        {project.title}
      </Link>
      <div className="project-progress-bar-container">
        <div className="project-progress-bar">
          {milestonePositions.map((milestone, idx) => {
            const segmentCompleted = milestone.task_count > 0
              ? (milestone.tasks_completed / milestone.task_count) * milestone.width
              : (milestone.completed ? milestone.width : 0)

            return (
              <div
                key={milestone.id}
                className={`progress-segment ${milestone.completed ? 'completed' : ''}`}
                style={{ width: `${milestone.width}%` }}
                onMouseEnter={() => setHoveredMilestone(milestone.id)}
                onMouseLeave={() => setHoveredMilestone(null)}
              >
                <div
                  className="progress-segment-fill"
                  style={{ width: `${(milestone.tasks_completed / (milestone.task_count || 1)) * 100}%` }}
                />
                {/* Milestone marker */}
                {idx < milestonePositions.length - 1 && (
                  <div className="milestone-marker" />
                )}
                {/* Tooltip */}
                {hoveredMilestone === milestone.id && (
                  <div className="milestone-tooltip">
                    <strong>{milestone.title}</strong>
                    <span>{milestone.tasks_completed}/{milestone.task_count} tasks</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="project-progress-percent">
          {project.completion_percent.toFixed(0)}%
        </div>
      </div>
    </div>
  )
}

function Dashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      const data = await fetchDashboardStats()
      setStats(data)
      setError(null)
    } catch (err) {
      setError('Failed to load dashboard statistics')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>
  }

  if (!stats) {
    return null
  }

  return (
    <div className="dashboard">
      <div className="elise-header">
        <h1>Project Dashboard</h1>
        <div className="header-actions">
          <button onClick={loadStats} className="btn btn-primary">
            <RefreshIcon size={18} /> Refresh
          </button>
        </div>
      </div>

      {/* Overview Cards - Simplified */}
      <div className="dashboard-grid compact">
        <div className="stat-card">
          <div className="stat-icon">
            <IdeaIcon size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.overview.total_ideas}</div>
            <div className="stat-label">Ideas</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <ClipboardIcon size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.overview.total_plans}</div>
            <div className="stat-label">Plans</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <RocketIcon size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.overview.active_plans}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <CheckIcon size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.overview.completed_plans}</div>
            <div className="stat-label">Done</div>
          </div>
        </div>
      </div>

      {/* Project Progress Visualization */}
      {stats.project_progress && stats.project_progress.length > 0 && (
        <div className="dashboard-section">
          <h2>Project Progress</h2>
          <div className="project-progress-list">
            {stats.project_progress.map(project => (
              <ProjectProgressBar key={project.id} project={project} />
            ))}
          </div>
        </div>
      )}

      {/* Cost Analysis - Simplified */}
      {stats.costs.plans_with_cost_tracking > 0 && (
        <div className="dashboard-section">
          <h2>Budget Overview</h2>
          <div className="cost-grid compact">
            <div className="cost-card">
              <div className="cost-label">Estimated</div>
              <div className="cost-value">${stats.costs.total_estimated.toFixed(0)}</div>
            </div>
            <div className="cost-card">
              <div className="cost-label">Actual</div>
              <div className="cost-value">${stats.costs.total_actual.toFixed(0)}</div>
            </div>
            <div className="cost-card">
              <div className="cost-label">Variance</div>
              <div className={`cost-value ${stats.costs.total_variance > 0 ? 'over' : 'under'}`}>
                {stats.costs.total_variance > 0 ? '+' : ''}${stats.costs.total_variance.toFixed(0)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity - Compact */}
      {stats.recent_activity.length > 0 && (
        <div className="dashboard-section">
          <h2>Recent Activity</h2>
          <div className="activity-list compact">
            {stats.recent_activity.slice(0, 5).map((activity, index) => (
              <div key={index} className="activity-item compact">
                <span className="activity-type">{activity.type}</span>
                <span className="activity-title">
                  {activity.title || activity.summary}
                </span>
                <span className="activity-date">
                  {new Date(activity.timestamp).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
